import { useShellContext } from '@omni/kit';
import { Optional } from '@omni/kit/Types';
import {
  KitButton,
  KitInternationalPhoneNumberInput,
  KitSnack,
  KitText,
  KitTouchable,
  KitTouchableIcon,
  KitVerifyCodeInput,
} from '@omni/kit/components';
import countries from '@omni/kit/components/KitInternationalPhoneNumberInput/constants/countries.json';
import { Country } from '@omni/kit/components/KitInternationalPhoneNumberInput/constants/types';
import KitModalV2 from '@omni/kit/components/KitModalV2';
import {
  KitSnackDuration,
  KitSnackRender,
} from '@omni/kit/components/KitSnack';
import Show from '@omni/kit/components/Show';
import useShellAuthProviders from '@omni/kit/contexts/ShellContext/hooks/useShellAuthProviders';
import { SizeClassV2, useSizeClass } from '@omni/kit/contexts/SizeClassContext';
import { useCountdownTimer } from '@omni/kit/hooks/useCountdownTimer';
import { useTranslation } from '@omni/kit/hooks/useTranslation';
import BaseService, { IHttpResponse } from '@omni/kit/services/BaseService';
import BorderRadius from '@omni/kit/theming/BorderRadius';
import Colors from '@omni/kit/theming/Colors';
import Spacing from '@omni/kit/theming/Spacing';
import { notifyAppUserSettingsUpdated } from '@omni/kit/utilities/NativeHelpers';
import sanitizePhoneNumber from '@omni/kit/utilities/sanitizePhoneNumber';
import {
  capitalizeFirstLetter,
  handleHttpErrorResponse,
} from '@omni/kit/utilities/utilities';
import React, { useEffect, useMemo, useState } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';

import { useBlockPageContext } from '../../../../BlockPageContext';
import { UpdateActionInterface } from '../../../types';

export interface ChangePhoneNumberModalProps {
  appKey: string;
  clientBrandColor?: string;
  phoneNumber: string;
  profileID?: string;
  setRefreshing?: (refreshing: boolean) => void;
  setVisible: (visible: boolean) => void;
  /** The action to perform when a user changes their phone number. */
  updatePhoneNumberAction?: UpdateActionInterface;
  visible: boolean;
  phoneNumberObj: {
    significant: string;
    country: { region_code: string; calling_code: string };
  };
}

const CLOSE_BUTTON_SIZE = 32;

const ChangePhoneNumberModal: React.FC<ChangePhoneNumberModalProps> = ({
  appKey,
  phoneNumber,
  profileID,
  setRefreshing,
  setVisible,
  updatePhoneNumberAction,
  visible,
  phoneNumberObj,
}): JSX.Element => {
  const { t } = useTranslation(['blocks']);

  const { tokens } = useShellContext();
  const { targetProviders } = useShellAuthProviders();
  const { authProviderId } = useBlockPageContext();

  const { sizeClassV2 } = useSizeClass();

  const isMobileDevice = sizeClassV2 < SizeClassV2.M;

  const targetProviderToken = useMemo(
    () =>
      authProviderId
        ? tokens.accessTokens[authProviderId] || undefined
        : undefined,
    [authProviderId, tokens.accessTokens]
  );

  const [value, setValue] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [snackOptions, setSnackOptions] = useState({
    visible: false,
    message: '',
    duration: KitSnackDuration.SHORT,
    marginBottom: Spacing.l,
  });

  const [step, setStep] = useState<number>(1);
  const [showError, setShowError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [newPhoneNumber, setNewPhoneNumber] = useState<string>('');
  const [country, setCountry] = useState<Country | undefined>(undefined);
  const {
    counter,
    isActive: isResendCodeDisabled,
    start: startCountdown,
    stop: stopCountdown,
  } = useCountdownTimer();

  const handleVerificationCode = (
    success = true,
    error: Optional<string> = null
  ): void => {
    /**
     *  Based on phone-number-verification-code request we decide what interactions should
     *  happen for the user regarding the error type (verificationCodeInvalid, codeExpired or tooManyAttempts)
     */
    setIsLoading(false);
    setValue('');

    if (success) {
      setRefreshing?.(true);
      onClose();
      KitSnack.show(t('blocks:changePhoneNumber.phoneNumberUpdated'));
    } else {
      if (error === 'verification_code_invalid') {
        showKitSnack(
          t('blocks:changePhoneNumber.error.invalid'),
          KitSnackDuration.SHORT
        );
      } else if (error === 'verification_code_expired') {
        showKitSnack(
          t('blocks:changePhoneNumber.error.expired'),
          KitSnackDuration.SHORT
        );
      } else if (error === 'verification_code_too_many_attempts') {
        KitSnack.show(t('blocks:changePhoneNumber.error.tooManyAttempts'));
      } else if (error === 'phone_number_already_exists') {
        KitSnack.show(t('blocks:changePhoneNumber.error.phoneNumberExists'));
      } else {
        showKitSnack(
          t('blocks:changePhoneNumber.error.default'),
          KitSnackDuration.SHORT
        );
      }
    }
  };

  const handlePhoneNumberUpdated = (
    success = true,
    message: Optional<string> = null
  ): void => {
    if (success) {
      // setVisible(false);
      // Server takes a while before returning the updated data
      setTimeout(() => {
        setRefreshing?.(true); // auto-refresh the entire screen to display new phone number if provided by backend
        notifyAppUserSettingsUpdated();
      }, 500);
    } else {
      showKitSnack(
        message?.length
          ? capitalizeFirstLetter(message)
          : t('blocks:changePhoneNumber.error.failedSave'),
        KitSnackDuration.SHORT
      );
    }
  };

  const makeRequestForChangePhoneNumber = async (): Promise<IHttpResponse> => {
    // make a request with feeds endpoint to update phone number
    const originalAction = updatePhoneNumberAction;

    if (originalAction?.updateUrl && originalAction?.settings?.[0]) {
      const settings = [...(originalAction?.settings ?? [{}])];
      settings[0] = {
        ...settings[0],
        value: sanitizePhoneNumber(country?.callingCode + newPhoneNumber),
      };

      const url = originalAction.updateUrl;

      return await BaseService.Put({
        url: url as string,
        appKey,
        authProviderId,
        targetProviders,
        token: targetProviderToken,
        data: { profileID, settings },
        headers: {
          'Content-Type': 'application/json',
        },
      });
    } else {
      throw new Error();
    }
  };

  const saveNewPhoneNumber = async (): Promise<void> => {
    if (!country) {
      setErrorMessage(t('blocks:changePhoneNumber.error.countryRequired'));
      setShowError(true);

      return;
    }

    setIsLoading(true);
    const res = await makeRequestForChangePhoneNumber();

    const ok = res.status === 201 || res.status === 200;

    if (ok) {
      handlePhoneNumberUpdated(true);
    } else {
      const { message } = await handleHttpErrorResponse(res);
      handlePhoneNumberUpdated(false, message);
    }

    setStep(2);
    setValue('');
    setIsLoading(false);
  };

  const resendCode = async (): Promise<void> => {
    const res = await makeRequestForChangePhoneNumber();

    const ok = res.status === 201 || res.status === 200;

    startCountdown();

    if (ok) {
      showKitSnack(
        t('blocks:changePhoneNumber.phoneNumberResent'),
        KitSnackDuration.SHORT
      );
    } else {
      const { message } = await handleHttpErrorResponse(res);
      showKitSnack(
        message ?? t('blocks:changePhoneNumber.error.failedSave'),
        KitSnackDuration.SHORT
      );
    }
  };

  const showKitSnack = (msg: string, dur: number): void => {
    setSnackOptions({
      duration: dur,
      marginBottom: Spacing.l,
      message: msg,
      visible: true,
    });
  };

  const isPhoneNumberValid = useMemo((): boolean => {
    const digitsOnly = newPhoneNumber.replace(/\D/g, '');

    return digitsOnly.length === country?.length;
  }, [newPhoneNumber, country]);

  const getPhoneNumber = useMemo((): string => {
    const newLine = isMobileDevice ? '\n' : '';

    return `${newLine}${country?.callingCode} ${newPhoneNumber}`;
  }, [country, newPhoneNumber, isMobileDevice]);

  const confirmVerificationCode = async (): Promise<void> => {
    setIsLoading(true);

    const originalAction = updatePhoneNumberAction;

    if (originalAction?.confirmPhoneUrl) {
      const url = originalAction.confirmPhoneUrl;

      const res = await BaseService.Put({
        url: url as string,
        appKey,
        authProviderId,
        targetProviders,
        token: targetProviderToken,
        data: { code: value, phone_number: phoneNumber },
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const ok = res.status === 201 || res.status === 200;

      if (ok) {
        handleVerificationCode();
      } else {
        const { data } = await handleHttpErrorResponse(res);
        handleVerificationCode(false, data?.error);
      }
    }

    setIsLoading(false);
  };

  const onClose = () => {
    setVisible(false);
    setNewPhoneNumber('');
    setCountry(undefined);
    setShowError(false);
    setErrorMessage('');
    setValue('');
    stopCountdown();
  };

  const renderContent = () => (
    <View style={styles.container}>
      <KitText
        extraBold
        fontSize={24}
        lineHeight={28}
        fontWeight={900}
        color={Colors.N900}
        style={{
          marginRight: !isMobileDevice ? Spacing.xxl : 0,
        }}
      >
        {step === 1
          ? t('blocks:changePhoneNumber.updatePhoneNumber')
          : t('blocks:changePhoneNumber.verifyCodeNumber')}
      </KitText>
      <View style={styles.contentContainer}>
        {step === 1 ? (
          <>
            <KitInternationalPhoneNumberInput
              autoFocus={true}
              label={t('blocks:changePhoneNumber.mobilePhoneNumber')}
              onChangeText={(text) => {
                setNewPhoneNumber(text);
                setShowError(false);
              }}
              value={newPhoneNumber}
              selectedCountry={country}
              onCountryChange={(country) => {
                setCountry(country);
                const digitsOnly = newPhoneNumber.replace(/\D/g, '');

                if (digitsOnly.length > country.length) {
                  setShowError(true);
                  setErrorMessage(
                    t('blocks:changePhoneNumber.error.confirmNumber')
                  );
                  setNewPhoneNumber(digitsOnly.slice(0, country.length));
                } else {
                  setShowError(false);
                  setNewPhoneNumber(digitsOnly);
                }
              }}
              placeholderTextColor={Colors.N200}
              countries={countries}
              style={{ marginBottom: Spacing.xl }}
            />
            <Show show={newPhoneNumber.length === 0 ? false : showError}>
              <View style={styles.errorContainer}>
                <KitText color={Colors.N0} fontSize={16}>
                  {errorMessage}
                </KitText>
              </View>
            </Show>
            <View
              style={{
                gap: Spacing.m,
                ...(!isMobileDevice && {
                  flexDirection: 'row-reverse',
                }),
              }}
            >
              <KitButton
                color={Colors.N1000}
                disabled={
                  (newPhoneNumber.length === 0
                    ? false
                    : country && !isPhoneNumberValid) || isLoading
                }
                onPress={saveNewPhoneNumber}
                isLoading={isLoading}
                title={t('blocks:changePhoneNumber.continue')}
              />
              <KitButton secondary onPress={onClose} title='Cancel' />
            </View>
          </>
        ) : (
          <>
            {country?.callingCode ? (
              <KitText
                color={Colors.N900}
                fontSize={18}
                lineHeight={24}
                style={{
                  marginBottom: Spacing.l,
                }}
              >
                {t('blocks:changePhoneNumber.enterCodeNumber', {
                  phoneNumber: getPhoneNumber,
                })}
              </KitText>
            ) : null}

            <KitVerifyCodeInput setValue={setValue} value={value}>
              <View
                pointerEvents={isLoading ? 'none' : 'auto'}
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'flex-end',
                  opacity: isLoading ? 0.5 : 1,
                  marginBottom: Spacing.l,
                  marginTop: Spacing.m,
                  ...(!isMobileDevice && {
                    marginRight: '22%',
                  }),
                }}
              >
                {!isResendCodeDisabled ? (
                  <KitTouchable onPress={resendCode} noRadius>
                    <KitText
                      color={Colors.N500}
                      style={{
                        textDecorationLine: 'underline',
                      }}
                    >
                      {t('blocks:changePhoneNumber.resendCodeShort')}
                    </KitText>
                  </KitTouchable>
                ) : (
                  <KitText color={Colors.N300} fontSize={16}>
                    {t('blocks:changePhoneNumber.requestNewCodeIn', {
                      seconds: counter,
                    })}
                  </KitText>
                )}
              </View>
              <View
                style={{
                  gap: Spacing.m,
                  ...(!isMobileDevice && {
                    flexDirection: 'row-reverse',
                  }),
                }}
              >
                <KitButton
                  color={Colors.N1000}
                  disabled={isLoading || value.length !== 6}
                  onPress={confirmVerificationCode}
                  isLoading={isLoading}
                  title={t('blocks:changePhoneNumber.continue')}
                />
                <KitButton secondary onPress={onClose} title='Cancel' />
              </View>
            </KitVerifyCodeInput>
          </>
        )}
      </View>
    </View>
  );

  useEffect((): void => {
    if (
      visible &&
      phoneNumberObj.country.region_code &&
      phoneNumberObj.significant
    ) {
      setNewPhoneNumber(phoneNumberObj.significant);
      setCountry(
        countries.find(
          (country: Country) =>
            country.regionCode === phoneNumberObj.country.region_code
        ) ?? countries[0]
      );
    } else if (phoneNumber) {
      setNewPhoneNumber(phoneNumber);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  useEffect((): void => {
    if (country && newPhoneNumber.length > country.length) {
      const digitsOnly = newPhoneNumber.replace(/\D/g, '');

      setNewPhoneNumber(digitsOnly.slice(0, country.length));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country]);

  useEffect(() => {
    if (visible) {
      setStep(1);
    }
  }, [visible]);

  return (
    <KitModalV2
      anchorBottom={isMobileDevice}
      avoidKeyboard={true}
      isVisible={visible}
      modalStyle={styles.modalStyle}
      backdropStyle={styles.backdropStyle}
      onClose={onClose}
      coverScreen
    >
      {!isMobileDevice ? (
        <KitTouchableIcon
          name='remove'
          color={Colors.N900}
          underlayColor={Colors.N100}
          buttonSize={CLOSE_BUTTON_SIZE}
          style={styles.closeButton}
          onPress={onClose}
          size={14}
        />
      ) : null}
      <KeyboardAwareScrollView
        style={{ flex: 1 }}
        contentContainerStyle={styles.scrollContainer}
        keyboardShouldPersistTaps='handled'
        enableOnAndroid={true}
        showsVerticalScrollIndicator={false}
        enableAutomaticScroll={Platform.OS === 'android'}
      >
        {renderContent()}
      </KeyboardAwareScrollView>

      <KitSnackRender
        {...snackOptions}
        setVisible={(value) =>
          setSnackOptions({ ...snackOptions, visible: value })
        }
      />
    </KitModalV2>
  );
};

const styles = StyleSheet.create({
  modalStyle: {
    width: '100%',
    maxWidth: 600,
    paddingTop: 0,
    paddingHorizontal: 0,
    paddingBottom: 0,
    alignSelf: 'center',
  },
  backdropStyle: {
    alignItems: undefined,
  },
  scrollContainer: {
    flexGrow: 1,
    justifyContent: 'center',
  },
  container: {
    paddingTop: Spacing.xxl,
    paddingHorizontal: Spacing.xl,
    paddingBottom: Spacing.xl,
  },
  contentContainer: {
    marginTop: Spacing.xl,
  },
  errorContainer: {
    paddingHorizontal: Spacing.l,
    paddingVertical: Spacing.m,
    backgroundColor: Colors.R500,
    borderRadius: BorderRadius.m,
    marginBottom: Spacing.l,
  },
  cell: {
    backgroundColor: Colors.N75,
    borderRadius: BorderRadius.m,
    color: Colors.N900,
    fontSize: 32,
    height: 54,
    overflow: 'hidden',
    paddingTop: Platform.OS === 'ios' ? 8 : 5,
    textAlign: 'center',
    width: 44,
  },
  focusCell: { color: Colors.brand },
  closeButton: {
    position: 'absolute',
    right: Spacing.xl,
    top: Spacing.xl,
    zIndex: 1,
  },
});

export default ChangePhoneNumberModal;
